import React, { useState } from 'react'
import { GitCompare } from 'lucide-react'
import { AnalyticsLayout } from '@/components/analytics/AnalyticsLayout'
import { ScorecardTable, ScorecardRow } from '@/components/analytics/ScorecardTable'
import { ScorecardCompareModal } from '@/components/analytics/ScorecardCompareModal'
import { ExportButton } from '@/components/shared/ExportButton'
import { ChartInfoButton } from '@/components/shared/ChartInfoButton'
import { useAnalyticsQuery } from '@/hooks/useAnalyticsQuery'
import { useAnalyticsFilters } from '@/contexts/AnalyticsFiltersContext'

interface ScorecardResponse {
  kind: 'architect'
  minProjects: number
  rows: ScorecardRow[]
}

function ArchitectBody() {
  const { queryParams } = useAnalyticsFilters()
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const [compareOpen, setCompareOpen] = useState(false)
  const { data, isLoading, error } = useAnalyticsQuery<ScorecardResponse>(
    'scorecards.architects',
    '/analytics/scorecards/architects',
  )

  const canCompare = selectedIds.length >= 2 && selectedIds.length <= 4

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-2">
          <div>
            <h2 className="text-lg font-semibold text-secondary-900">Architect scorecard</h2>
            <p className="text-xs text-secondary-500">
              Hidden until an architect has at least {data?.minProjects ?? 3} projects. Lighter metric set
              — architects are judged by portfolio breadth and pricing percentile more than velocity.
              Select 2–4 architects to compare trends.
            </p>
          </div>
          <ChartInfoButton
            title="Architect scorecard"
            info={{
              definition:
                'Per-architect portfolio snapshot. Focus on breadth (projects, units, GMV) and pricing positioning (€/m² percentile) rather than velocity metrics, since architects rarely control absorption.',
              formula:
                'GMV = SUM(current_price of sold units) · median €/m² percentile = rank of architect\'s median €/m² vs the universe · pipeline value = SUM(current_price of available units) · projects = COUNT(DISTINCT project)',
              calculation:
                'A minimum-projects threshold (default 3) hides architects with thin track records to avoid noise. Compare mode pulls weekly time series for up to 4 selected architects — useful to see if an architect consistently launches at the top of their price band.',
              source: 'unit, project, architect',
            }}
          />
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setCompareOpen(true)}
            disabled={!canCompare}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-secondary-300 bg-white text-secondary-700 hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            title={
              canCompare
                ? `Compare ${selectedIds.length} selected architects`
                : 'Select 2–4 architects to enable compare mode'
            }
          >
            <GitCompare className="h-4 w-4" />
            Compare ({selectedIds.length})
          </button>
          <ExportButton
            endpoint="/analytics/export/scorecards/architects.csv"
            params={queryParams}
            filename="analytics-scorecards-architects"
          />
        </div>
      </div>
      <ScorecardTable
        kind="architect"
        rows={data?.rows}
        loading={isLoading}
        error={error}
        selectable
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
      />
      {compareOpen && canCompare && (
        <ScorecardCompareModal
          kind="architect"
          ids={selectedIds}
          onClose={() => setCompareOpen(false)}
        />
      )}
    </div>
  )
}

export function ArchitectScorecards() {
  return (
    <AnalyticsLayout filterVariant="architect">
      <ArchitectBody />
    </AnalyticsLayout>
  )
}
